import { Label, Text } from '@amzn/stencil-react-components/text';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import uuid from 'uuid';
import { triggerTestEmail } from '../../actions/createScheduleAction';
import { ChannelType, Frequency } from '../../enums/schedulingConstant';
import ButtonWithLoader from '../atoms/ButtonWithLoader';
import { Select } from '@amzn/stencil-react-components/select';
import { Col, Row, Spacer, View } from '@amzn/stencil-react-components/layout';
import { buildSegmentListOption, segmentListFilter } from '../../util/campaign/campaignUtils';
import { updateTestEmailDetails } from '../../actions/createScheduleAction';
import { CampaignFormSection } from '../../enums/schedulingConstant';
import { getAllSegments } from '../../actions/nudgeActions';
import { Spinner, SpinnerSize } from '@amzn/stencil-react-components/spinner';
import { ResponseStatus } from '../../enums/responseStatus';
import isEmpty from 'lodash/isEmpty';
import { TEST_EMAIL_TOOLTIP_TEXT } from '../../constants/messages';
import { withTooltip } from '@amzn/stencil-react-components/tooltip';

import { IconExclamationCircle } from '../../mocks/stencilV4IconMocks';
import { IconCheckCircleFillSmall } from '@amzn/stencil-react-icons';
import { IconQuestionCircleSmall } from '@amzn/stencil-react-icons';

const IconWithTooltip = withTooltip()(IconQuestionCircleSmall);

const SendTestCampaign = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const templateDetails = useSelector(store => store.campaign.campaignFormState.templateDetails);
  const segmentDetails = useSelector(store => store.campaign.campaignFormState.segmentDetails);
  const testEmailDetails = useSelector(store => store.campaign.testEmailDetails);
  const userInfo = useSelector(store => store.userInfo);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSegment, setIsLoadingSegment] = useState(false);
  const [isTestEmailTriggered, setIsTestEmailTriggered] = useState(false);
  const [isTestEmailSuccessful, setIsTestEmailSuccessful] = useState(false);
  const segmentList = useSelector(store => store.nudge.segmentList);
  const isSubmitSuccess = useSelector(store => store.campaign.campaignFormState.isSubmitSuccess);
  const buildSegmentList = buildSegmentListOption(segmentList);
  const segmentValueAccessor = option => option.id;
  const segmentsRenderOption = option => (
    <Row display="flex" flexDirection="column" gridGap="S200">
      <Row alignItems="center">{option?.name}</Row>
      <Text fontSize="T100" color="neutral70">
        {option?.description}
      </Text>
    </Row>
  );
  const [isValid, setIsValid] = useState(false);

  const segmentFilterOption = searchText => segmentListFilter(searchText, buildSegmentList);

  const [selectedSegment, setSelectedSegment] = useState({
    name: testEmailDetails?.segmentName ?? '',
    id: testEmailDetails?.segmentId ?? '',
  });
  const onSelectSegment = e => {
    setSelectedSegment(e);
    setIsTestEmailTriggered(false);
    dispatch(
      updateTestEmailDetails(CampaignFormSection.SEGMENT, {
        segmentName: e?.name,
        segmentId: e?.id,
      }),
    );
  };

  const refreshSegmentList = async () => {
    setIsLoadingSegment(true);
    await dispatch(getAllSegments(userInfo.selectedAccessGroupName, userInfo.idToken));
    setIsLoadingSegment(false);
  };

  const payload = {
    userGroupName: userInfo?.selectedAccessGroupName,
    userName: userInfo.user,
    campaign: {
      campaignIdentifier: {
        campaignName: uuid.v4(),
        campaignDescription: '-',
      },
      channel: ChannelType.EMAIL ?? '',
      createdBy: userInfo.user,
      endTime: '',
      frequency: Frequency.ONCE,
      lastUpdatedBy: userInfo.user,
      messageTemplateName: templateDetails.messageTemplateName?.name ?? '',
      fromAddress: templateDetails.fromAddress ?? '',
      segmentIdentifier: {
        segmentId: testEmailDetails.segmentId,
        segmentName: testEmailDetails.segmentName,
      },
      startTime: 'IMMEDIATE',
      timeZone: '',
    },
  };

  const onSendTestEmail = async () => {
    setIsTestEmailTriggered(true);
    setIsLoading(true);
    await dispatch(triggerTestEmail(payload, userInfo.idToken));
    setIsLoading(false);
  };

  useEffect(
    () => {
      const status = testEmailDetails?.status;
      if (status === ResponseStatus.SUCCESS) {
        setIsTestEmailSuccessful(true);
      } else {
        setIsTestEmailSuccessful(false);
      }
    },
    [testEmailDetails.status],
  );

  useEffect(
    () => {
      if (!isEmpty(templateDetails?.fromAddress) && !isEmpty(segmentDetails.segmentId)) {
        setIsValid(true);
      } else {
        setIsValid(false);
      }
    },
    [templateDetails?.fromAddress, testEmailDetails.segmentId],
  );

  return (
    <Col display="flex" flexDirection="column" gridGap="S300">
      <Col>
        <Text fontSize={'T400'} color="primaryLabelText">
          <FormattedMessage
            id="app.campaign.template.label.sendTestEmail"
            defaultMessage="Send a test email"
          />
          <IconWithTooltip
            title="Show tooltip"
            tooltipText={TEST_EMAIL_TOOLTIP_TEXT}
            color="primary70"
          />
        </Text>
      </Col>

      <Col>
        <Col gridGap="S200">
          <Label htmlFor="choose-segment" id="choose-segment-label">
            <FormattedMessage
              id="app.campaign.label.chooseASegment"
              defaultMessage="Choose a segment"
            />
          </Label>
          <Select
            error={false}
            id="choose-segment"
            data-testid="select-segment-test-dp"
            isMulti={false}
            isSearchable={true}
            labelId="choose-segment-label"
            disabled={isSubmitSuccess}
            onChange={e => {
              onSelectSegment(e);
            }}
            options={buildSegmentList}
            value={selectedSegment}
            valueAccessor={segmentValueAccessor}
            renderOption={segmentsRenderOption}
            filterOptions={segmentFilterOption}
            noOptionsText="No options found."
            placeholder="Select a segment for campaign"
          />
          <View
            display={'flex'}
            style={{ color: '#23756b', cursor: 'pointer' }}
            alignItems={'center'}
            onClick={refreshSegmentList}
            className={isLoading ? 'disabled' : ''}
          >
            <FormattedMessage id="app.button.refresh" defaultMessage="Refresh" />
            <Spacer width={10} />
            {isLoadingSegment && <Spinner size={SpinnerSize.Small} />}
          </View>
        </Col>
        <Spacer height={20} />
        {isTestEmailTriggered &&
          !isLoading &&
          isTestEmailSuccessful && (
            <Label color="green" display="flex">
              <FormattedMessage
                id="app.testEmail.message.success"
                defaultMessage="Test email send successfully"
              />
              <Spacer width={10} />
              <IconCheckCircleFillSmall />
            </Label>
          )}
        {isTestEmailTriggered &&
          !isLoading &&
          !isTestEmailSuccessful && (
            <Label color="#b11010" display="flex">
              <FormattedMessage
                id="app.testEmail.message.error"
                defaultMessage="Something went wrong. Please try again."
              />
              <Spacer width={10} />
              <IconExclamationCircle />
            </Label>
          )}
        <Spacer height={20} />
        <View display="flex" flexDirection="row">
          <ButtonWithLoader
            data-testid="send-test-email"
            isLoading={isLoading}
            isDisabled={isLoading || isSubmitSuccess || !isValid}
            isPrimary={false}
            onClickHandler={onSendTestEmail}
          >
            <FormattedMessage id="app.button.testEmail" defaultMessage={'Test Email'} />
          </ButtonWithLoader>
          <Spacer width="10px" />
          <IconWithTooltip
            title="Preview Email"
            tooltipText={formatMessage({ id: 'app.icon.testEmailTooltip' })}
            color="primary70"
          />
        </View>
      </Col>
    </Col>
  );
};

export default SendTestCampaign;
